import React, { useState, useRef, useEffect } from 'react';
import { representanteAPI, pagosAPI, conceptosPagoAPI } from '../../services/api';
import { useOCR } from './OcrProcessor';

const API_URL = process.env.REACT_APP_API_URL || 'https://lossaliasfutbolclub.com/api';

const metodosPago = [
  { value: 'pago_movil', label: 'Pago Móvil', icon: '📱' },
  { value: 'transferencia', label: 'Transferencia', icon: '🏦' },
  { value: 'efectivo_bs', label: 'Efectivo Bs', icon: '💵' },
  { value: 'efectivo_usd', label: 'Efectivo USD', icon: '💲' },
  { value: 'zelle', label: 'Zelle', icon: '💸' },
];

const RegistrarPago = ({ jugadores, representanteId, onPagoRegistrado }) => {
  const [step, setStep] = useState(1);
  const [selectedJugador, setSelectedJugador] = useState(null);
  const [conceptosDisponibles, setConceptosDisponibles] = useState([]);
  const [selectedConcepto, setSelectedConcepto] = useState(null);
  const [modoPago, setModoPago] = useState('divisas'); // 'divisas' = USD cash, 'bolivares' = Bs at BCV rate
  const [metodoPago, setMetodoPago] = useState('');
  const [comprobante, setComprobante] = useState(null);
  const [comprobantePreview, setComprobantePreview] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [tasaBCV, setTasaBCV] = useState(null);
  const [tasaEUR, setTasaEUR] = useState(null);
  const [tasaFecha, setTasaFecha] = useState(null); // Date the rate corresponds to
  const [tasaExacta, setTasaExacta] = useState(true); // Whether the rate is exact for that date
  const [loadingTasa, setLoadingTasa] = useState(false);

  // Payment data form (step 4)
  const [formData, setFormData] = useState({
    monto_bs: '',
    monto_usd: '',
    referencia: '',
    banco_origen: '',
    telefono_origen: '',
    fecha_pago: new Date().toISOString().split('T')[0],
  });

  // Payment status per concept (how much has been paid already)
  const [conceptoStatus, setConceptoStatus] = useState(null); // { totalPagado, montoRestante, pagadoCompleto, cantidadPagos }
  const [conceptosStatusMap, setConceptosStatusMap] = useState({}); // { conceptoId: statusData }
  const [loadingConceptoStatus, setLoadingConceptoStatus] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { processReceipt, processing, progress, error: ocrError } = useOCR();

  // Load BCV rate and conceptos
  useEffect(() => {
    const loadData = async () => {
      try {
        const [tasaRes, conceptosRes] = await Promise.all([
          pagosAPI.getTasaBCV(),
          conceptosPagoAPI.getAll({ activo: true })
        ]);
        setTasaBCV(tasaRes.data.tasa);
        setTasaEUR(tasaRes.data.tasaEUR);
        setConceptosDisponibles(conceptosRes.data);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, []);

  // Auto-calculate USD when BS changes
  useEffect(() => {
    if (formData.monto_bs && tasaBCV) {
      const usd = (parseFloat(formData.monto_bs) / tasaBCV).toFixed(2);
      setFormData(prev => ({ ...prev, monto_usd: usd }));
    }
  }, [formData.monto_bs, tasaBCV]);

  // Fetch BCV rate for the payment date when it changes
  useEffect(() => {
    const fetchTasaPorFecha = async () => {
      if (!formData.fecha_pago) return;
      setLoadingTasa(true);
      try {
        const res = await pagosAPI.getTasaPorFecha(formData.fecha_pago);
        setTasaBCV(res.data.tasa);
        setTasaEUR(res.data.tasaEUR);
        setTasaFecha(formData.fecha_pago);
        setTasaExacta(res.data.tasaExacta);
        // Recalculate amounts with new rate
        if (selectedConcepto && modoPago) {
          // Will be handled by the monto_bs useEffect above
        }
      } catch (err) {
        console.error('Error fetching rate for date:', err);
      } finally {
        setLoadingTasa(false);
      }
    };
    fetchTasaPorFecha();
  }, [formData.fecha_pago]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load payment status for all concepts when player is selected
  useEffect(() => {
    const loadConceptosStatus = async () => {
      if (!selectedJugador || conceptosDisponibles.length === 0) return;
      try {
        const statusPromises = conceptosDisponibles.map(c =>
          pagosAPI.getConceptoStatus(selectedJugador.id, c.id)
            .then(res => ({ conceptoId: c.id, ...res.data }))
            .catch(() => null)
        );
        const results = await Promise.all(statusPromises);
        const map = {};
        results.forEach(r => {
          if (r) map[r.conceptoId] = r;
        });
        setConceptosStatusMap(map);
      } catch (err) {
        console.error('Error loading concepts status:', err);
      }
    };
    loadConceptosStatus();
  }, [selectedJugador, conceptosDisponibles]);

  // Check if concept has Bs pricing
  const conceptoHasBs = (concepto) => {
    return concepto && concepto.precio_bs_valor && parseFloat(concepto.precio_bs_valor) > 0;
  };

  // Get divisas (USD) price for the jugador
  const getPrecioDivisas = (concepto) => {
    if (!concepto || !selectedJugador) return null;
    const isMediaBeca = selectedJugador.status === 'media_beca';
    const isBecado = selectedJugador.status === 'becado';
    if (isBecado) return { monto: 0, label: 'Becado' };
    const precio = isMediaBeca && concepto.precio_media_beca_usd
      ? parseFloat(concepto.precio_media_beca_usd)
      : parseFloat(concepto.precio_usd);
    return {
      monto: precio,
      label: isMediaBeca && concepto.precio_media_beca_usd ? 'Media Beca' : 'Regular'
    };
  };

  // Get Bs price for the jugador
  const getPrecioBs = (concepto) => {
    if (!concepto || !selectedJugador || !conceptoHasBs(concepto)) return null;
    const isMediaBeca = selectedJugador.status === 'media_beca';
    const isBecado = selectedJugador.status === 'becado';
    if (isBecado) return { valor: 0, tasa: concepto.tasa_bs || 'USD', label: 'Becado' };
    const valor = isMediaBeca && concepto.precio_bs_media_beca_valor
      ? parseFloat(concepto.precio_bs_media_beca_valor)
      : parseFloat(concepto.precio_bs_valor);
    return {
      valor: valor,
      tasa: concepto.tasa_bs || 'USD',
      label: isMediaBeca && concepto.precio_bs_media_beca_valor ? 'Media Beca' : 'Regular'
    };
  };

  // When selecting a concepto, check payment status and pre-fill amount
  const handleSelectConcepto = async (concepto) => {
    setSelectedConcepto(concepto);
    setModoPago('divisas');

    // Use the pre-loaded status map
    const status = conceptosStatusMap[concepto.id];
    setConceptoStatus(status || null);

    if (status) {
      // If fully paid, don't prefill (shouldn't reach here since button is disabled)
      if (status.pagadoCompleto) return;
      // If partially paid, prefill with remaining amount
      if (status.totalPagado > 0) {
        prefillMontoConSaldo(concepto, 'divisas', status.montoRestante);
        return;
      }
    }

    prefillMonto(concepto, 'divisas');
  };

  // When changing modoPago, recalculate and reset metodoPago if incompatible
  const handleModoPagoChange = (modo) => {
    setModoPago(modo);
    // Clear metodo if it's not compatible with the new mode
    const bsMethods = ['pago_movil', 'transferencia', 'efectivo_bs'];
    const divisasMethods = ['efectivo_usd', 'zelle'];
    if (modo === 'divisas' && bsMethods.includes(metodoPago)) setMetodoPago('');
    if (modo === 'bolivares' && divisasMethods.includes(metodoPago)) setMetodoPago('');
    if (selectedConcepto) {
      if (conceptoStatus && conceptoStatus.totalPagado > 0 && !conceptoStatus.pagadoCompleto) {
        prefillMontoConSaldo(selectedConcepto, modo, conceptoStatus.montoRestante);
      } else {
        prefillMonto(selectedConcepto, modo);
      }
    }
  };

  // Check if a payment method is allowed for the current modoPago
  const isMetodoAllowed = (metodoValue) => {
    if (modoPago === 'divisas') {
      return ['efectivo_usd', 'zelle'].includes(metodoValue);
    }
    // bolivares
    return ['pago_movil', 'transferencia', 'efectivo_bs'].includes(metodoValue);
  };

  // Pre-fill with remaining balance (for partial payments / abonos)
  const prefillMontoConSaldo = (concepto, modo, montoRestanteUSD) => {
    if (modo === 'divisas') {
      setFormData(prev => ({
        ...prev,
        monto_usd: montoRestanteUSD.toFixed(2),
        monto_bs: tasaBCV ? (montoRestanteUSD * tasaBCV).toFixed(2) : '',
      }));
    } else {
      // For bolivares mode, convert remaining USD to Bs using the concept's tasa
      const pBs = getPrecioBs(concepto);
      if (pBs && pBs.valor > 0) {
        const pDiv = getPrecioDivisas(concepto);
        if (pDiv && pDiv.monto > 0) {
          // Proportional: remaining ratio applied to Bs price
          const ratio = montoRestanteUSD / pDiv.monto;
          const bsValor = pBs.valor * ratio;
          const tasa = pBs.tasa === 'EUR' ? tasaEUR : tasaBCV;
          if (tasa) {
            const bs = (bsValor * tasa).toFixed(2);
            const usd = tasaBCV ? (parseFloat(bs) / tasaBCV).toFixed(2) : '';
            setFormData(prev => ({
              ...prev,
              monto_bs: bs,
              monto_usd: usd,
            }));
            return;
          }
        }
      }
      // Fallback: just use USD remaining converted to Bs
      setFormData(prev => ({
        ...prev,
        monto_usd: montoRestanteUSD.toFixed(2),
        monto_bs: tasaBCV ? (montoRestanteUSD * tasaBCV).toFixed(2) : '',
      }));
    }
  };

  const prefillMonto = (concepto, modo) => {
    if (modo === 'divisas') {
      const precio = getPrecioDivisas(concepto);
      if (!precio || precio.monto <= 0) return;
      setFormData(prev => ({
        ...prev,
        monto_usd: precio.monto.toFixed(2),
        monto_bs: tasaBCV ? (precio.monto * tasaBCV).toFixed(2) : '',
      }));
    } else {
      // bolivares
      const precio = getPrecioBs(concepto);
      if (!precio || precio.valor <= 0) return;
      const tasa = precio.tasa === 'EUR' ? tasaEUR : tasaBCV;
      if (tasa) {
        const bs = (precio.valor * tasa).toFixed(2);
        const usd = tasaBCV ? (parseFloat(bs) / tasaBCV).toFixed(2) : '';
        setFormData(prev => ({
          ...prev,
          monto_bs: bs,
          monto_usd: usd,
        }));
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setComprobante(file);
    setComprobantePreview(URL.createObjectURL(file));

    // Auto OCR for pago_movil and transferencia
    if (metodoPago === 'pago_movil' || metodoPago === 'transferencia') {
      const result = await processReceipt(file);
      setOcrResult(result);

      if (result.success && result.hasData) {
        setFormData(prev => ({
          ...prev,
          referencia: result.data.referencia || prev.referencia,
          monto_bs: result.data.monto ? String(result.data.monto) : prev.monto_bs,
          fecha_pago: result.data.fecha || prev.fecha_pago,
          telefono_origen: result.data.telefono || prev.telefono_origen,
          banco_origen: result.data.banco || prev.banco_origen,
        }));
      }
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);

    try {
      // 1. Upload comprobante if exists
      let comprobanteUrl = null;
      if (comprobante) {
        const formDataUpload = new FormData();
        formDataUpload.append('comprobante', comprobante);
        const uploadRes = await representanteAPI.uploadComprobante(formDataUpload);
        comprobanteUrl = uploadRes.data.filename;
      }

      // 2. Create payment
      const pagoData = {
        jugador_id: selectedJugador.id,
        representante_id: representanteId,
        concepto: selectedConcepto?.tipo || 'otro',
        concepto_id: selectedConcepto?.id || null,
        descripcion: selectedConcepto?.nombre || null,
        monto_bs: parseFloat(formData.monto_bs) || null,
        monto_usd: parseFloat(formData.monto_usd) || null,
        metodo_pago: metodoPago,
        referencia: formData.referencia || null,
        comprobante_url: comprobanteUrl,
        fecha_pago: formData.fecha_pago,
        mes_correspondiente: selectedConcepto?.mes_correspondiente || null,
        ano_correspondiente: selectedConcepto?.ano_correspondiente || null,
        estado: 'pendiente',
        banco_origen: formData.banco_origen || null,
        telefono_origen: formData.telefono_origen || null,
      };

      await pagosAPI.create(pagoData);
      setSuccess(true);

      if (onPagoRegistrado) onPagoRegistrado();
    } catch (err) {
      console.error('Error submitting payment:', err);
      const errorData = err.response?.data;
      if (errorData?.conceptoPagado) {
        setError(`Este concepto ya está completamente pagado. Total pagado: $${errorData.totalPagado?.toFixed(2)}`);
        setStep(2); // Go back to concept selection
      } else if (errorData?.excedeMonto) {
        setError(`El monto excede el saldo restante ($${errorData.montoRestante?.toFixed(2)}). Ajuste el monto.`);
        setStep(4); // Go back to amount form
      } else {
        setError(errorData?.error || 'Error al registrar el pago. Intente de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedJugador(null);
    setSelectedConcepto(null);
    setModoPago('divisas');
    setMetodoPago('');
    setComprobante(null);
    setComprobantePreview(null);
    setOcrResult(null);
    setTasaFecha(null);
    setTasaExacta(true);
    setConceptoStatus(null);
    setConceptosStatusMap({});
    setFormData({
      monto_bs: '',
      monto_usd: '',
      referencia: '',
      banco_origen: '',
      telefono_origen: '',
      fecha_pago: new Date().toISOString().split('T')[0],
    });
    setError('');
    setSuccess(false);
  };

  // Success screen
  if (success) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <span className="text-4xl">✅</span>
        </div>
        <h3 className="text-xl font-bold text-green-800 mt-4">¡Pago Registrado!</h3>
        <p className="text-gray-500 mt-2 text-sm">
          Tu pago ha sido registrado y está pendiente de confirmación por el administrador.
        </p>
        <button
          onClick={resetForm}
          className="mt-6 px-6 py-3 bg-lsfc-green-600 text-white rounded-xl font-medium hover:bg-lsfc-green-700 transition-colors"
        >
          Registrar otro pago
        </button>
      </div>
    );
  }

  // Step indicator
  const totalSteps = 5;
  const stepLabels = ['Jugador', 'Concepto', 'Comprobante', 'Datos', 'Confirmar'];

  // Group conceptos by tipo
  const conceptosByTipo = conceptosDisponibles.reduce((acc, c) => {
    if (!acc[c.tipo]) acc[c.tipo] = [];
    acc[c.tipo].push(c);
    return acc;
  }, {});

  const tipoIcons = {
    mensualidad: '📅',
    torneo: '🏆',
    inscripcion: '📝',
    uniforme: '👕',
    otro: '📋',
  };

  const tipoLabels = {
    mensualidad: 'Mensualidades',
    torneo: 'Torneos',
    inscripcion: 'Inscripción',
    uniforme: 'Uniformes',
    otro: 'Otros',
  };

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-2">
        {stepLabels.map((label, idx) => {
          const stepNum = idx + 1;
          const isActive = step === stepNum;
          const isCompleted = step > stepNum;
          return (
            <div key={label} className="flex flex-col items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                isActive ? 'bg-lsfc-green-600 text-white' :
                isCompleted ? 'bg-lsfc-green-200 text-lsfc-green-800' :
                'bg-gray-200 text-gray-400'
              }`}>
                {isCompleted ? '✓' : stepNum}
              </div>
              <span className={`text-xs mt-1 ${isActive ? 'text-lsfc-green-600 font-medium' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-lsfc-green-600 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        ></div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
        </div>
      )}

      {/* STEP 1: Select player */}
      {step === 1 && (
        <div className="space-y-3">
          <h3 className="font-bold text-gray-800">Selecciona el jugador</h3>
          {jugadores.map((j) => (
            <button
              key={j.id}
              onClick={() => { setSelectedJugador(j); setStep(2); }}
              className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 transition-colors ${
                selectedJugador?.id === j.id
                  ? 'border-lsfc-green-500 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-lsfc-green-300'
              }`}
            >
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                {j.foto_url ? (
                  <img
                    src={`${API_URL.replace('/api', '')}/uploads/documentos/${j.foto_url}`}
                    alt={j.nombres}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-2xl">⚽</span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">{j.nombres} {j.apellidos}</p>
                <p className="text-xs text-gray-400">{j.edad} años • {j.cedula || 'Sin cédula'}</p>
              </div>
              {j.status === 'media_beca' && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">🎓 Media Beca</span>
              )}
              {j.status === 'becado' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">🎓 Becado</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* STEP 2: Select concept and payment method */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800">¿Qué vas a pagar?</h3>
            {selectedJugador?.status && selectedJugador.status !== 'regular' && (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                selectedJugador.status === 'media_beca'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                🎓 {selectedJugador.status === 'media_beca' ? 'Media Beca' : 'Becado'}
              </span>
            )}
          </div>

          {conceptosDisponibles.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <p className="text-3xl mb-2">📋</p>
              <p className="text-gray-500 text-sm">No hay conceptos de pago disponibles</p>
              <p className="text-gray-400 text-xs mt-1">Contacte al administrador</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(conceptosByTipo).map(([tipo, items]) => (
                <div key={tipo}>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {tipoIcons[tipo]} {tipoLabels[tipo] || tipo}
                  </p>
                  <div className="space-y-2">
                    {items.map((c) => {
                      const isSelected = selectedConcepto?.id === c.id;
                      const cStatus = conceptosStatusMap[c.id];
                      const isPagado = cStatus?.pagadoCompleto;
                      const hasAbono = cStatus && cStatus.totalPagado > 0 && !cStatus.pagadoCompleto;
                      return (
                        <button
                          key={c.id}
                          onClick={() => !isPagado && handleSelectConcepto(c)}
                          disabled={isPagado}
                          className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                            isPagado
                              ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                              : isSelected
                                ? 'border-lsfc-green-500 bg-green-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-lsfc-green-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <p className={`font-medium text-sm truncate ${isPagado ? 'text-gray-400' : 'text-gray-800'}`}>{c.nombre}</p>
                              {c.mes_correspondiente && (
                                <p className="text-xs text-gray-400">{c.mes_correspondiente} {c.ano_correspondiente}</p>
                              )}
                              {c.descripcion && (
                                <p className="text-xs text-gray-400 truncate">{c.descripcion}</p>
                              )}
                              {/* Payment status badges */}
                              {isPagado && (
                                <p className="text-xs text-green-600 font-medium mt-1">✅ Pagado completo</p>
                              )}
                              {hasAbono && (
                                <p className="text-xs text-amber-600 font-medium mt-1">
                                  💰 Abonado: ${cStatus.totalPagado.toFixed(2)} / ${cStatus.precioConcepto.toFixed(2)} — Resta: ${cStatus.montoRestante.toFixed(2)}
                                </p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              {isPagado ? (
                                <p className="font-bold text-green-500">✅</p>
                              ) : (() => {
                                const pDiv = getPrecioDivisas(c);
                                const pBs = getPrecioBs(c);
                                if (pDiv?.label === 'Becado') return <p className="font-bold text-green-600">Becado</p>;
                                if (!pDiv || pDiv.monto <= 0) return <p className="text-gray-400 text-sm">$0.00</p>;
                                return (
                                  <>
                                    <p className="font-bold text-green-700">${pDiv.monto.toFixed(2)}</p>
                                    {pBs && pBs.valor > 0 && (() => {
                                      const tasa = pBs.tasa === 'EUR' ? tasaEUR : tasaBCV;
                                      return (
                                        <p className="text-xs text-blue-600">
                                          {tasa ? `${(pBs.valor * tasa).toFixed(0)} Bs` : ''}
                                        </p>
                                      );
                                    })()}
                                    {pDiv.label !== 'Regular' && (
                                      <p className="text-xs text-amber-600 font-medium">{pDiv.label}</p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          {/* Show regular price strikethrough for media beca */}
                          {selectedJugador?.status === 'media_beca' && c.precio_media_beca_usd && (() => {
                            const precioRegular = parseFloat(c.precio_usd);
                            const precioBeca = parseFloat(c.precio_media_beca_usd);
                            if (!precioBeca || !precioRegular) return null;
                            return (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-xs text-gray-400 line-through">
                                  Regular: ${precioRegular.toFixed(2)}
                                </span>
                                <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">
                                  -{Math.round((1 - precioBeca / precioRegular) * 100)}%
                                </span>
                              </div>
                            );
                          })()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Abono info banner */}
          {selectedConcepto && conceptoStatus && conceptoStatus.totalPagado > 0 && !conceptoStatus.pagadoCompleto && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm">
              <p className="font-medium text-amber-800">💰 Este concepto tiene abonos previos</p>
              <div className="mt-1 text-amber-700 text-xs space-y-0.5">
                <p>Precio total: <strong>${conceptoStatus.precioConcepto.toFixed(2)}</strong></p>
                <p>Ya pagado: <strong>${conceptoStatus.totalPagado.toFixed(2)}</strong> ({conceptoStatus.cantidadPagos} pago{conceptoStatus.cantidadPagos !== 1 ? 's' : ''})</p>
                <p>Saldo restante: <strong className="text-amber-900">${conceptoStatus.montoRestante.toFixed(2)}</strong></p>
              </div>
              {/* Progress bar */}
              <div className="mt-2 w-full bg-amber-200 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (conceptoStatus.totalPagado / conceptoStatus.precioConcepto) * 100)}%` }}
                ></div>
              </div>
            </div>
          )}

          {loadingConceptoStatus && (
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-500 flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
              Verificando pagos previos...
            </div>
          )}

          {/* Payment mode selector: Divisas vs Bolivares */}
          {selectedConcepto && (() => {
            const pDiv = getPrecioDivisas(selectedConcepto);
            const pBs = getPrecioBs(selectedConcepto);
            if (!pDiv || pDiv.monto <= 0) return null;

            return (
              <div className="mt-2 space-y-2">
                <h3 className="font-bold text-gray-800">¿Como deseas pagar?</h3>
                <div className={`grid ${pBs && pBs.valor > 0 ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                  {/* Divisas option */}
                  <button
                    type="button"
                    onClick={() => handleModoPagoChange('divisas')}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      modoPago === 'divisas'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-green-300'
                    }`}
                  >
                    <p className="text-lg mb-1">💵</p>
                    <p className="text-xs font-bold text-gray-800">Divisas (USD)</p>
                    <p className="text-sm font-bold text-green-700 mt-1">${pDiv.monto.toFixed(2)}</p>
                    {pDiv.label !== 'Regular' && (
                      <p className="text-xs text-amber-600 font-medium">{pDiv.label}</p>
                    )}
                  </button>

                  {/* Bolivares option */}
                  {pBs && pBs.valor > 0 && (() => {
                    const tasa = pBs.tasa === 'EUR' ? tasaEUR : tasaBCV;
                    const symbol = pBs.tasa === 'EUR' ? '\u20AC' : '$';
                    return (
                      <button
                        type="button"
                        onClick={() => handleModoPagoChange('bolivares')}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          modoPago === 'bolivares'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                      >
                        <p className="text-lg mb-1">🏦</p>
                        <p className="text-xs font-bold text-gray-800">Bolivares</p>
                        <p className={`text-sm font-bold mt-1 ${pBs.tasa === 'EUR' ? 'text-blue-700' : 'text-amber-700'}`}>
                          {symbol}{pBs.valor.toFixed(2)}
                        </p>
                        {tasa && (
                          <p className="text-xs text-gray-400">
                            = {(pBs.valor * tasa).toFixed(0)} Bs (tasa {pBs.tasa === 'EUR' ? 'Euro' : 'Dolar'})
                          </p>
                        )}
                        {pBs.label !== 'Regular' && (
                          <p className="text-xs text-amber-600 font-medium">{pBs.label}</p>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            );
          })()}

          <h3 className="font-bold text-gray-800 mt-4">Metodo de pago</h3>
          <div className="grid grid-cols-2 gap-3">
            {metodosPago.map((m) => {
              const allowed = isMetodoAllowed(m.value);
              return (
                <button
                  key={m.value}
                  onClick={() => allowed && setMetodoPago(m.value)}
                  disabled={!allowed}
                  className={`p-3 rounded-xl border-2 text-center transition-colors ${
                    !allowed
                      ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                      : metodoPago === m.value
                        ? 'border-lsfc-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-lsfc-green-300'
                  }`}
                >
                  <span className="text-xl">{m.icon}</span>
                  <p className="text-xs font-medium mt-1">{m.label}</p>
                </button>
              );
            })}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              Atrás
            </button>
            <button
              onClick={() => { if (selectedConcepto && metodoPago) setStep(3); }}
              disabled={!selectedConcepto || !metodoPago}
              className="flex-1 py-3 bg-lsfc-green-600 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Receipt photo */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-bold text-gray-800">
            {metodoPago === 'pago_movil' || metodoPago === 'transferencia'
              ? 'Foto del comprobante de pago'
              : metodoPago === 'efectivo_bs' || metodoPago === 'efectivo_usd'
              ? 'Foto de los billetes (opcional)'
              : 'Comprobante de pago (opcional)'}
          </h3>

          {(metodoPago === 'pago_movil' || metodoPago === 'transferencia') && (
            <p className="text-xs text-gray-500">
              📸 Toma una foto clara del comprobante. El sistema intentará leer los datos automáticamente.
            </p>
          )}

          {/* Image preview */}
          {comprobantePreview && (
            <div className="relative">
              <img
                src={comprobantePreview}
                alt="Comprobante"
                className="w-full max-h-64 object-contain rounded-xl border border-gray-200"
              />
              <button
                onClick={() => {
                  setComprobante(null);
                  setComprobantePreview(null);
                  setOcrResult(null);
                }}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm"
              >
                ✕
              </button>
            </div>
          )}

          {/* OCR progress */}
          {processing && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-blue-700 font-medium">Procesando comprobante...</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-blue-500 mt-1">{progress}%</p>
            </div>
          )}

          {/* OCR result */}
          {ocrResult && !processing && (
            <div className={`p-3 rounded-xl border text-sm ${
              ocrResult.hasData
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-yellow-50 border-yellow-200 text-yellow-700'
            }`}>
              {ocrResult.hasData ? (
                <>
                  <p className="font-medium">✅ Datos detectados del comprobante</p>
                  <p className="text-xs mt-1">Los datos se pre-llenaron. Puedes editarlos en el siguiente paso.</p>
                </>
              ) : (
                <>
                  <p className="font-medium">⚠️ No se pudieron detectar datos</p>
                  <p className="text-xs mt-1">Ingresa los datos manualmente en el siguiente paso.</p>
                </>
              )}
            </div>
          )}

          {ocrError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {ocrError}
            </div>
          )}

          {/* Upload buttons */}
          {!comprobantePreview && (
            <div className="flex gap-3">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="flex-1 py-4 bg-lsfc-green-600 text-white rounded-xl font-medium flex flex-col items-center gap-1"
              >
                <span className="text-2xl">📸</span>
                <span className="text-sm">Tomar foto</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 py-4 bg-white border-2 border-lsfc-green-500 text-lsfc-green-700 rounded-xl font-medium flex flex-col items-center gap-1"
              >
                <span className="text-2xl">📁</span>
                <span className="text-sm">Elegir archivo</span>
              </button>
            </div>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep(2)}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              Atrás
            </button>
            <button
              onClick={() => setStep(4)}
              disabled={processing || ((metodoPago === 'pago_movil' || metodoPago === 'transferencia') && !comprobante)}
              className="flex-1 py-3 bg-lsfc-green-600 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: Payment data form */}
      {step === 4 && (
        <div className="space-y-4">
          <h3 className="font-bold text-gray-800">Datos del pago</h3>

          {/* Show expected amount */}
          {selectedConcepto && (() => {
            if (modoPago === 'divisas') {
              const pDiv = getPrecioDivisas(selectedConcepto);
              if (!pDiv || pDiv.monto <= 0) return null;
              return (
                <div className="p-3 rounded-xl text-sm bg-green-50 border border-green-200">
                  <p className="text-green-700">
                    💵 Monto esperado: <strong>${pDiv.monto.toFixed(2)}</strong> USD
                    {tasaBCV && <span> = <strong>Bs. {(pDiv.monto * tasaBCV).toFixed(2)}</strong></span>}
                    {pDiv.label !== 'Regular' && (
                      <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{pDiv.label}</span>
                    )}
                  </p>
                </div>
              );
            } else {
              const pBs = getPrecioBs(selectedConcepto);
              if (!pBs || pBs.valor <= 0) return null;
              const tasa = pBs.tasa === 'EUR' ? tasaEUR : tasaBCV;
              const symbol = pBs.tasa === 'EUR' ? '\u20AC' : '$';
              return (
                <div className={`p-3 rounded-xl text-sm ${pBs.tasa === 'EUR' ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className={pBs.tasa === 'EUR' ? 'text-blue-700' : 'text-amber-700'}>
                    🏦 Monto esperado: <strong>{symbol}{pBs.valor.toFixed(2)}</strong>
                    {tasa && <span> = <strong>Bs. {(pBs.valor * tasa).toFixed(2)}</strong></span>}
                    <span className="text-xs text-gray-500 ml-1">(tasa BCV/{pBs.tasa === 'EUR' ? 'Euro' : 'Dolar'})</span>
                    {pBs.label !== 'Regular' && (
                      <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{pBs.label}</span>
                    )}
                  </p>
                </div>
              );
            }
          })()}

          {/* Abono warning in step 4 */}
          {conceptoStatus && conceptoStatus.totalPagado > 0 && !conceptoStatus.pagadoCompleto && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
              <p className="font-medium">⚠️ Este pago es un abono. Saldo pendiente: <strong>${conceptoStatus.montoRestante.toFixed(2)}</strong></p>
              <p className="text-xs mt-0.5">El monto no debe exceder el saldo restante.</p>
            </div>
          )}

          {ocrResult?.hasData && (
            <p className="text-xs text-green-600">
              ✅ Datos pre-llenados del comprobante. Verifica y corrige si es necesario.
            </p>
          )}

          {/* Amount BS */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Monto en Bs *</label>
            <input
              type="number"
              step="0.01"
              value={formData.monto_bs}
              onChange={(e) => setFormData({ ...formData, monto_bs: e.target.value })}
              placeholder="0.00"
              className="w-full p-3 border border-gray-200 rounded-xl text-sm"
            />
          </div>

          {/* Rate info badge */}
          {tasaBCV && (
            <div className={`p-2 rounded-lg text-xs flex items-center gap-2 ${
              tasaExacta ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
            }`}>
              {loadingTasa ? (
                <span>⏳ Cargando tasa...</span>
              ) : (
                <>
                  <span>{tasaExacta ? '✅' : '⚠️'}</span>
                  <span>
                    Tasa BCV/Dólar{tasaFecha ? ` del ${new Date(tasaFecha + 'T12:00:00').toLocaleDateString('es-VE')}` : ''}: <strong>{tasaBCV.toFixed(2)} Bs/$</strong>
                    {tasaEUR && <span className="ml-2">| Euro: <strong>{tasaEUR.toFixed(2)} Bs/€</strong></span>}
                    {!tasaExacta && <span className="ml-1">(aprox.)</span>}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Amount USD (auto-calculated) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Equivalente USD {tasaBCV && `(Tasa: ${tasaBCV.toFixed(2)})`}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.monto_usd}
              onChange={(e) => setFormData({ ...formData, monto_usd: e.target.value })}
              placeholder="0.00"
              className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha del pago *</label>
            <input
              type="date"
              value={formData.fecha_pago}
              onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
              className="w-full p-3 border border-gray-200 rounded-xl text-sm"
            />
          </div>

          {/* Additional fields for pago_movil / transferencia */}
          {(metodoPago === 'pago_movil' || metodoPago === 'transferencia') && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nro. de Referencia</label>
                <input
                  type="text"
                  value={formData.referencia}
                  onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                  placeholder="Ej: 123456789"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Banco origen</label>
                <input
                  type="text"
                  value={formData.banco_origen}
                  onChange={(e) => setFormData({ ...formData, banco_origen: e.target.value })}
                  placeholder="Ej: Mercantil"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono origen</label>
                <input
                  type="text"
                  value={formData.telefono_origen}
                  onChange={(e) => setFormData({ ...formData, telefono_origen: e.target.value })}
                  placeholder="Ej: 04121234567"
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                />
              </div>
            </>
          )}

          {/* Zelle reference */}
          {metodoPago === 'zelle' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nro. de Confirmación Zelle</label>
              <input
                type="text"
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                placeholder="Confirmation number"
                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep(3)}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              Atrás
            </button>
            <button
              onClick={() => {
                if (formData.monto_bs && formData.fecha_pago) setStep(5);
                else setError('Monto y fecha son requeridos');
              }}
              className="flex-1 py-3 bg-lsfc-green-600 text-white rounded-xl font-medium"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Summary and confirm */}
      {step === 5 && (
        <div className="space-y-4">
          <h3 className="font-bold text-gray-800">Resumen del pago</h3>

          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {/* Player */}
            <div className="p-4 flex items-center gap-3">
              <span className="text-xl">👤</span>
              <div>
                <p className="text-xs text-gray-400">Jugador</p>
                <p className="font-medium text-gray-800">{selectedJugador?.nombres} {selectedJugador?.apellidos}</p>
              </div>
            </div>

            {/* Concept */}
            <div className="p-4 flex items-center gap-3">
              <span className="text-xl">{tipoIcons[selectedConcepto?.tipo] || '📋'}</span>
              <div>
                <p className="text-xs text-gray-400">Concepto</p>
                <p className="font-medium text-gray-800">
                  {selectedConcepto?.nombre}
                  {selectedConcepto?.mes_correspondiente && ` - ${selectedConcepto.mes_correspondiente} ${selectedConcepto.ano_correspondiente}`}
                </p>
              </div>
            </div>

            {/* Method */}
            <div className="p-4 flex items-center gap-3">
              <span className="text-xl">{metodosPago.find(m => m.value === metodoPago)?.icon}</span>
              <div>
                <p className="text-xs text-gray-400">Método de pago</p>
                <p className="font-medium text-gray-800">
                  {metodosPago.find(m => m.value === metodoPago)?.label}
                </p>
              </div>
            </div>

            {/* Amount */}
            <div className="p-4 flex items-center gap-3">
              <span className="text-xl">💰</span>
              <div>
                <p className="text-xs text-gray-400">Monto ({modoPago === 'divisas' ? 'Divisas' : 'Bolivares'})</p>
                {modoPago === 'divisas' ? (
                  <>
                    <p className="font-bold text-green-700 text-lg">
                      ${parseFloat(formData.monto_usd || 0).toFixed(2)} USD
                    </p>
                    <p className="text-xs text-gray-500">
                      ≈ Bs. {parseFloat(formData.monto_bs || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-gray-800 text-lg">
                      Bs. {parseFloat(formData.monto_bs || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">
                      ≈ ${parseFloat(formData.monto_usd || 0).toFixed(2)} USD
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Date */}
            <div className="p-4 flex items-center gap-3">
              <span className="text-xl">📅</span>
              <div>
                <p className="text-xs text-gray-400">Fecha</p>
                <p className="font-medium text-gray-800">{formData.fecha_pago}</p>
              </div>
            </div>

            {/* Reference */}
            {formData.referencia && (
              <div className="p-4 flex items-center gap-3">
                <span className="text-xl">🔢</span>
                <div>
                  <p className="text-xs text-gray-400">Referencia</p>
                  <p className="font-medium text-gray-800">{formData.referencia}</p>
                </div>
              </div>
            )}

            {/* Bank */}
            {formData.banco_origen && (
              <div className="p-4 flex items-center gap-3">
                <span className="text-xl">🏦</span>
                <div>
                  <p className="text-xs text-gray-400">Banco origen</p>
                  <p className="font-medium text-gray-800">{formData.banco_origen}</p>
                </div>
              </div>
            )}

            {/* Comprobante preview */}
            {comprobantePreview && (
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-2">Comprobante adjunto</p>
                <img
                  src={comprobantePreview}
                  alt="Comprobante"
                  className="w-32 h-32 object-contain rounded-lg border border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Abono indicator in summary */}
          {conceptoStatus && conceptoStatus.totalPagado > 0 && !conceptoStatus.pagadoCompleto && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              💰 Este pago es un <strong>abono</strong>. Pagado anteriormente: ${conceptoStatus.totalPagado.toFixed(2)} de ${conceptoStatus.precioConcepto.toFixed(2)}
            </div>
          )}

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-700">
            ⚠️ El pago quedará en estado <strong>Pendiente</strong> hasta que el administrador lo confirme.
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setStep(4)}
              disabled={submitting}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              Atrás
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 bg-lsfc-green-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                'Enviar Pago'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrarPago;
